import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import MarketingShell from "@/components/MarketingShell";
import { PHOTOS } from "@/lib/photos";
import { logDebug } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EASE = [0.4, 0, 0.2, 1];
const WHATSAPP_URL = "https://chat.whatsapp.com/BXx47XXin66GbaAfo21jIz";

const NEXT = [
  {
    title: "Your name goes up",
    body: "It lands on the Patrons wall within a few minutes — first name, last initial and where you're from. Producers sit at the top and largest.",
    to: "/patrons#wall",
    cta: "See the wall",
    testId: "welcome-next-wall",
  },
  {
    title: "The WhatsApp invite",
    body: "The behind-the-scenes community: voice notes from the piano, works in progress, half-finished ideas. The link is in your welcome email too — or jump straight in.",
    href: WHATSAPP_URL,
    cta: "Join the WhatsApp",
    testId: "welcome-next-whatsapp",
  },
  {
    title: "A shoutout on the podcast",
    body: "Dan thanks new patrons by name on the next episode. Start with this week's if you haven't heard it yet.",
    to: "/podcast",
    cta: "This week's episode",
    testId: "welcome-next-podcast",
  },
];

const goldCta =
  "inline-flex items-center justify-center rounded-full px-6 h-12 text-xs uppercase tracking-[0.22em]";
const plainCta =
  "inline-flex items-center justify-center rounded-full px-6 h-12 text-xs uppercase tracking-[0.22em] border border-white/25 transition-colors hover:bg-white/10";

export default function PatronWelcome() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  // Stripe redirects here the moment checkout closes; the webhook may land a
  // beat later, so poll until the payment is confirmed.
  const [state, setState] = useState(sessionId ? "checking" : "unknown");

  useEffect(() => {
    if (!sessionId) return undefined;
    let alive = true;
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (!alive) return;
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
      } catch (err) {
        // The record may not be readable for a tick; keep polling but leave a trace.
        logDebug("patron payment status poll failed", err);
      }
      if (alive && tries++ < 12) timer = setTimeout(poll, 2000);
      else if (alive) setState("slow");
    };
    poll();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [sessionId]);

  const statusLine = {
    checking: "Confirming your patronage with Stripe…",
    paid: "Payment confirmed. You're an active patron.",
    slow: "Stripe is still confirming. Your patronage will show up shortly — no need to pay again.",
    failed: "That payment didn't go through. Nothing was charged.",
  }[state];

  return (
    <MarketingShell active="/patrons">
      <section className="grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-16 items-center" data-testid="patron-welcome">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <p className="text-[10px] uppercase tracking-[0.32em] mb-4" style={{ color: "#F2D589" }}>
            You&apos;re in
          </p>
          <h1
            className="leading-[0.95]"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 500,
              fontSize: "clamp(40px, 5.2vw, 74px)",
              textWrap: "balance",
              maxWidth: "20ch",
            }}
            data-testid="patron-welcome-headline"
          >
            Thank you. Genuinely<span style={{ color: "#FCA07A" }}>.</span>
          </h1>
          <p className="mt-6 max-w-[44rem] text-base sm:text-lg leading-relaxed" style={{ color: "rgba(245,239,227,0.82)" }}>
            There&apos;s no label behind this music — just a piano, a method, and
            people like you who decided it should keep existing. You just became
            part of how the next piece gets made.
          </p>
          <p className="mt-4 max-w-[44rem] text-base leading-relaxed" style={{ color: "rgba(245,239,227,0.7)" }}>
            A welcome email is on its way with everything in one place. Cancel
            any time, no hard feelings — while you&apos;re in, the music has your
            name on it.
          </p>
          {statusLine ? (
            <p
              className="mt-6 text-sm"
              style={{ color: state === "failed" ? "#FCA07A" : "#F2D589" }}
              data-testid="patron-welcome-status"
            >
              {statusLine}
            </p>
          ) : null}
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/patrons#wall"
              className={goldCta}
              style={{ backgroundColor: "#F2D589", color: "#080419", fontWeight: 600 }}
              data-testid="patron-welcome-wall-cta"
            >
              See your name on the wall &rarr;
            </Link>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              className={plainCta}
              data-testid="patron-welcome-whatsapp-cta"
            >
              Join the WhatsApp
            </a>
            <Link to="/podcast" className={plainCta} data-testid="patron-welcome-podcast-cta">
              Listen to this week
            </Link>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          className="relative justify-self-center"
        >
          <img
            src={PHOTOS.danExcited}
            alt="Dan behind the scenes"
            className="block w-full h-auto"
            style={{ width: "min(520px, 92vw)" }}
            data-testid="patron-welcome-photo"
            loading="eager"
          />
          <div
            aria-hidden="true"
            className="absolute -inset-6 rounded-3xl"
            style={{
              background: "radial-gradient(60% 60% at 50% 50%, rgba(242,213,137,0.2) 0%, transparent 70%)",
              zIndex: -1,
              filter: "blur(8px)",
            }}
          />
        </motion.div>
      </section>

      <section className="mt-20" data-testid="patron-welcome-next">
        <h2 className="text-3xl sm:text-4xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
          What happens next.
        </h2>
        <ul className="mt-8 grid md:grid-cols-3 gap-5">
          {NEXT.map((n, i) => (
            <motion.li
              key={n.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, ease: EASE, delay: i * 0.08 }}
              className="rounded-2xl border p-6 backdrop-blur-md flex flex-col"
              style={{ borderColor: "rgba(255,255,255,0.12)", backgroundColor: "rgba(8,4,30,0.45)" }}
            >
              <h3 className="text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                {n.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: "rgba(245,239,227,0.72)" }}>
                {n.body}
              </p>
              {n.href ? (
                <a
                  href={n.href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={n.testId}
                  className="mt-6 text-[11px] uppercase tracking-[0.22em] transition-colors hover:text-[#F2D589]"
                  style={{ color: "#FCA07A" }}
                >
                  {n.cta} &rarr;
                </a>
              ) : n.to ? (
                <Link
                  to={n.to}
                  data-testid={n.testId}
                  className="mt-6 text-[11px] uppercase tracking-[0.22em] transition-colors hover:text-[#F2D589]"
                  style={{ color: "#FCA07A" }}
                >
                  {n.cta} &rarr;
                </Link>
              ) : null}
            </motion.li>
          ))}
        </ul>
        <p className="mt-8 text-sm" style={{ color: "rgba(245,239,227,0.6)" }}>
          Anything look wrong, or want your name shown differently?{" "}
          <Link to="/contact" className="underline underline-offset-4 hover:text-[#F2D589]" data-testid="patron-welcome-contact">
            Tell Dan
          </Link>
          .
        </p>
      </section>
    </MarketingShell>
  );
}
